import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { TreePine, MapPin, Clock, ChevronRight, ChevronLeft, Instagram } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { API, COMPANY } from "./constants";

const ProjectDetailModal = ({ isOpen, onClose, project }) => {
  const [photoIdx, setPhotoIdx] = useState(0);
  const photos = project?.photos?.length ? project.photos : (project?.image_url ? [project.image_url] : []);

  useEffect(() => { setPhotoIdx(0); }, [project?.id]);

  if (!project) return null;

  const prevPhoto = () => setPhotoIdx(i => (i - 1 + photos.length) % photos.length);
  const nextPhoto = () => setPhotoIdx(i => (i + 1) % photos.length);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        data-testid="project-detail-modal"
        className="bg-white border-0 max-w-5xl w-[95vw] p-0 rounded-2xl overflow-hidden"
      >
        <div className="flex flex-col md:flex-row">
          {/* Image side with arrows */}
          <div className="relative md:w-3/5 h-[300px] md:h-[500px] bg-stone-900">
            {photos[photoIdx] && (
              <img
                src={photos[photoIdx]}
                alt={`${project.title} - ${photoIdx + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            )}
            {photos.length > 1 && (
              <>
                <button
                  onClick={prevPhoto}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow-lg flex items-center justify-center hover:bg-white transition-colors z-10"
                  aria-label="Previous photo"
                  data-testid="modal-prev-photo"
                >
                  <ChevronLeft className="w-5 h-5 text-stone-700" />
                </button>
                <button
                  onClick={nextPhoto}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow-lg flex items-center justify-center hover:bg-white transition-colors z-10"
                  aria-label="Next photo"
                  data-testid="modal-next-photo"
                >
                  <ChevronRight className="w-5 h-5 text-stone-700" />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                  {photos.map((_, i) => (
                    <button key={i} onClick={() => setPhotoIdx(i)}
                      className={`w-2 h-2 rounded-full transition-all ${i === photoIdx ? "bg-white w-4" : "bg-white/50 hover:bg-white/80"}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
          {/* Info side - always visible */}
          <div className="md:w-2/5 bg-white p-8 flex flex-col justify-center">
            <div className="space-y-6">
              <div>
                <span className="inline-block bg-forest-100 text-forest-600 text-xs uppercase tracking-wider px-3 py-1 rounded-full mb-4">
                  {project.category}
                </span>
                <h2 className="font-serif text-2xl md:text-3xl text-forest-700 font-bold">{project.title}</h2>
              </div>
              <div className="space-y-4 border-t border-stone-200 pt-6">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-forest-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <TreePine className="w-4 h-4 text-forest-600" />
                  </div>
                  <div>
                    <p className="text-xs text-stone-400 uppercase tracking-wider">Work Type</p>
                    <p className="text-forest-700 font-medium">{project.work_type || "Landscape Service"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-forest-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-forest-600" />
                  </div>
                  <div>
                    <p className="text-xs text-stone-400 uppercase tracking-wider">Location</p>
                    <p className="text-forest-700 font-medium">{project.location || "Middle Georgia"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-forest-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="w-4 h-4 text-forest-600" />
                  </div>
                  <div>
                    <p className="text-xs text-stone-400 uppercase tracking-wider">Date</p>
                    <p className="text-forest-700 font-medium">{project.date || "2024"}</p>
                  </div>
                </div>
              </div>
              <div className="border-t border-stone-200 pt-6">
                <p className="text-xs text-stone-400 uppercase tracking-wider mb-2">Description</p>
                <p className="text-stone-600 leading-relaxed">{project.description}</p>
              </div>
              {photos.length > 1 && (
                <p className="text-xs text-stone-400 text-center">
                  Photo {photoIdx + 1} of {photos.length}
                </p>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const FALLBACK_PROJECTS = [
  { id: "work-1", title: "Pine Straw Installation", description: "Complete pine straw installation throughout the entire residence, enhancing curb appeal and protecting plant beds.", image_url: "https://customer-assets.emergentagent.com/job_landscape-portfolio-1/artifacts/9fmznoi5_pine-straw-warner-robins-march-2026.jpeg", category: "residential", work_type: "Pine Straw & Mulch", location: "Warner Robins, GA", date: "March 2026", photos: [] },
  { id: "work-2", title: "Black Mulch", description: "The job includes a general cleanup, shrub trimming, decorative brick edging, and black mulch replacement.", image_url: "https://customer-assets.emergentagent.com/job_landscape-portfolio-1/artifacts/rjrsru0k_georgia-outdoor-worx-garden-transformation.jpeg", category: "residential", work_type: "Mulch Installation", location: "Bonaire, GA", date: "March 2026", photos: [] },
  { id: "work-3", title: "Apartment Complex Maintenance", description: "Weekly maintenance program for residential apartment complexes, including lawn care, trimming, and seasonal cleanup.", image_url: "https://customer-assets.emergentagent.com/job_landscape-portfolio-1/artifacts/dubsnpxm_georgia-outdoor-worx-apt-complex.jpeg", category: "commercial", work_type: "Property Maintenance", location: "Macon, GA", date: "Weekly", photos: [] },
  { id: "work-4", title: "Drainage System Installation", description: "Complete drainage solution to improve water flow and prevent erosion and standing water issues.", image_url: "https://customer-assets.emergentagent.com/job_landscape-portfolio-1/artifacts/p7wcoea1_outdoor-drainage.avif", category: "residential", work_type: "Drainage Solutions", location: "Byron, GA", date: "September 2025", photos: [] }
];

export default function ProjectsSection() {
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const scrollRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const response = await axios.get(`${API}/portfolio`);
        const data = response.data.map(p => ({
          ...p,
          photos: p.photos?.length ? p.photos : (p.image_url ? [p.image_url] : []),
        }));
        setProjects(data);
      } catch (err) {
        setProjects(FALLBACK_PROJECTS);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const handleScroll = () => {
      const scrollLeft = container.scrollLeft;
      const cardWidth = container.scrollWidth / projects.length;
      const page = Math.round(scrollLeft / cardWidth);
      setCurrentPage(Math.min(page, projects.length - 1));
    };
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [projects.length]);

  const openProjectModal = (project) => { setSelectedProject(project); setModalOpen(true); };
  const closeProjectModal = () => { setModalOpen(false); setSelectedProject(null); };
  const scrollToPage = (index) => {
    const container = scrollRef.current;
    if (!container) return;
    const cardWidth = container.scrollWidth / projects.length;
    container.scrollTo({ left: cardWidth * index, behavior: 'smooth' });
    setCurrentPage(index);
  };

  if (isLoading) {
    return (
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="animate-pulse">Loading projects...</div>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="projects-section" className="py-20 md:py-28 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <span className="text-forest-500 font-medium tracking-wider uppercase text-sm">Latest Projects</span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-forest-700 font-bold mt-3 mb-4">
            Explore Our Case Studies &
            <span className="block">Latest Success Stories</span>
          </h2>
          <p className="text-stone-600 max-w-2xl mx-auto">
            Take a look at some of our recent landscape transformations across Middle Georgia. Click on any project to see more details.
          </p>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="larson-carousel flex gap-5 overflow-x-auto px-6 md:px-10 lg:px-12 pb-4"
        data-testid="projects-carousel"
      >
        {projects.map((project, index) => (
          <div
            key={project.id}
            data-testid={`project-card-${index}`}
            onClick={() => openProjectModal(project)}
            className="larson-card flex-shrink-0 cursor-pointer group"
          >
            <div className="larson-card__image relative overflow-hidden">
              <img
                src={project.photos?.[0] || project.image_url}
                alt={project.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent"></div>
              <h3 className="absolute bottom-5 left-5 right-5 font-serif text-xl md:text-2xl text-white font-bold leading-tight">
                {project.title}
              </h3>
              {project.photos?.length > 1 && (
                <span className="absolute top-3 right-3 bg-black/50 text-white text-xs px-2 py-0.5 rounded-full">
                  {project.photos.length} photos
                </span>
              )}
            </div>
            <div className="larson-card__content bg-white p-5">
              <p className="text-stone-500 text-sm leading-relaxed line-clamp-2 mb-4">{project.description}</p>
              <div className="flex items-center text-forest-600 font-medium text-sm group-hover:text-forest-700 transition-colors">
                <span>See project</span>
                <ChevronRight className="w-4 h-4 ml-1 transition-transform duration-300 group-hover:translate-x-1" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
        <div className="flex justify-center gap-2.5 mb-6">
          {projects.map((project, index) => (
            <button
              key={project.id || `dot-${index}`}
              onClick={() => scrollToPage(index)}
              data-testid={`carousel-dot-${index}`}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentPage ? 'bg-forest-500' : 'bg-stone-300 hover:bg-stone-400'
              }`}
              aria-label={`Go to project ${index + 1}`}
            />
          ))}
        </div>
        <div className="text-center">
          <a
            href={COMPANY.instagramLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="projects-instagram-link"
            className="inline-flex items-center gap-2 text-forest-600 hover:text-forest-700 font-medium transition-colors"
          >
            <Instagram className="w-5 h-5" />
            Follow us on Instagram for more projects
          </a>
        </div>
      </div>

      <ProjectDetailModal isOpen={modalOpen} onClose={closeProjectModal} project={selectedProject} />
    </section>
  );
}
